/**
 * 사물함 관리 (2026-09-02 신설 — 대표 지시)
 *
 * 🔴 사물함은 좌석이 아니다. 좌석으로 만들면 "학생 1명당 자리 하나" 규칙에 걸려
 *    열람실 자리 배정이 풀린다(부천신중동점이 실제로 막혔던 문제).
 *    그래서 별도 표(lockers)를 쓰고, 이 화면은 좌석 화면과 완전히 분리돼 있다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../infrastructure/api';

interface Locker {
  id: string;
  locker_number: number;
  label?: string | null;
  is_active: boolean;
  note?: string | null;
  student_id?: string | null;
  student_name?: string | null;
  user_number?: string | null;
  grade?: string | null;
  assigned_at?: string | null;
}

interface Member {
  user_id?: string;
  id?: string;
  name: string;
  grade?: string | null;
  user_number?: string | null;
}

type Filter = 'all' | 'available' | 'in_use';

const LockerManagement: React.FC = () => {
  const [lockers, setLockers] = useState<Locker[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  // 만들기 폼
  const [showCreate, setShowCreate] = useState(false);
  const [startNo, setStartNo] = useState('1');
  const [endNo, setEndNo] = useState('45');

  // 배정 패널
  const [picked, setPicked] = useState<Locker | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = (kind: 'ok' | 'err', text: string) => {
    setMsg({ kind, text });
    window.setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/v1/owner/lockers');
      const d: any = res.data ?? res;
      setLockers(d.lockers || []);
    } catch (e: any) {
      notify('err', '사물함 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 배정할 학생 목록 — 사물함은 좌석과 무관하므로 좌석 유무로 거르지 않는다.
  const loadMembers = useCallback(async () => {
    try {
      const res = await apiClient.get('/v1/owner/members?limit=500');
      const d: any = res.data ?? res;
      setMembers(d.members || []);
    } catch {
      setMembers([]);
    }
  }, []);

  const openAssign = (l: Locker) => {
    setPicked(l);
    setSearch('');
    if (members.length === 0) loadMembers();
  };

  const doCreate = async () => {
    const s = parseInt(startNo, 10);
    const e = parseInt(endNo, 10);
    if (!s || !e || e < s) { notify('err', '번호를 확인해 주세요.'); return; }
    try {
      setBusy(true);
      const res = await apiClient.post('/v1/owner/lockers/bulk', { start_number: s, end_number: e });
      const d: any = res.data ?? res;
      notify('ok', d.message || '만들었습니다.');
      setShowCreate(false);
      await load();
    } catch (e: any) {
      notify('err', e?.response?.data?.detail || '만들지 못했습니다.');
    } finally { setBusy(false); }
  };

  const doAssign = async (studentId: string) => {
    if (!picked) return;
    try {
      setBusy(true);
      const res = await apiClient.post(`/v1/owner/lockers/${picked.id}/assign`, { student_id: studentId });
      const d: any = res.data ?? res;
      notify('ok', d.message || '배정했습니다.');
      setPicked(null);
      await load();
    } catch (e: any) {
      notify('err', e?.response?.data?.detail || '배정하지 못했습니다.');
    } finally { setBusy(false); }
  };

  const doRelease = async (l: Locker) => {
    try {
      setBusy(true);
      await apiClient.post(`/v1/owner/lockers/${l.id}/release`);
      notify('ok', `${l.locker_number}번을 비웠습니다.`);
      setPicked(null);
      await load();
    } catch (e: any) {
      notify('err', e?.response?.data?.detail || '비우지 못했습니다.');
    } finally { setBusy(false); }
  };

  const shown = useMemo(() => lockers.filter((l) => {
    if (filter === 'available') return !l.student_id;
    if (filter === 'in_use') return !!l.student_id;
    return true;
  }), [lockers, filter]);

  const inUse = lockers.filter((l) => l.student_id).length;

  const filteredMembers = useMemo(() => {
    const q = search.trim();
    if (!q) return members.slice(0, 50);
    return members.filter((m) => (m.name || '').includes(q)).slice(0, 50);
  }, [members, search]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">사물함 관리</h1>
          <p className="text-sm text-gray-500 mt-1">
            사물함은 좌석과 별개입니다. 열람실 자리를 쓰는 학생에게도 그대로 배정할 수 있습니다.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          사물함 만들기
        </button>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          msg.kind === 'ok' ? 'bg-green-50 text-green-800 border border-green-200'
                            : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.text}
        </div>
      )}

      {showCreate && (
        <div className="mb-4 p-4 bg-gray-50 border rounded-lg">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">시작 번호</label>
              <input value={startNo} onChange={(e) => setStartNo(e.target.value)}
                     className="w-28 px-3 py-2 border rounded-lg" inputMode="numeric" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">끝 번호</label>
              <input value={endNo} onChange={(e) => setEndNo(e.target.value)}
                     className="w-28 px-3 py-2 border rounded-lg" inputMode="numeric" />
            </div>
            <button onClick={doCreate} disabled={busy}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
              만들기
            </button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 border rounded-lg">취소</button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            이미 있는 번호는 건너뜁니다. 여러 번 눌러도 중복으로 생기지 않습니다.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {([['all', `전체 ${lockers.length}`], ['in_use', `사용중 ${inUse}`],
           ['available', `비어있음 ${lockers.length - inUse}`]] as [Filter, string][]).map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    filter === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500">불러오는 중…</div>
      ) : lockers.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-gray-600 mb-2">아직 사물함이 없습니다.</p>
          <p className="text-sm text-gray-500">
            위 <b>사물함 만들기</b>에서 번호 범위를 넣어 주세요. (예: 1 ~ 45)
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {shown.map((l) => (
            <button
              key={l.id}
              onClick={() => openAssign(l)}
              className={`p-3 rounded-lg border text-left transition ${
                !l.is_active ? 'bg-gray-100 border-gray-300 opacity-60'
                : l.student_id ? 'bg-blue-50 border-blue-300 hover:border-blue-500'
                               : 'bg-white border-gray-200 hover:border-blue-400'}`}
            >
              <div className="text-lg font-bold text-gray-900">{l.locker_number}</div>
              <div className={`text-xs truncate mt-0.5 ${l.student_id ? 'text-blue-700' : 'text-gray-400'}`}>
                {!l.is_active ? '사용중지' : (l.student_name || '비어있음')}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* 배정 패널 — window.confirm/prompt 는 환경에 따라 막히므로 쓰지 않는다 */}
      {picked && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
             onClick={() => setPicked(null)}>
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h3 className="text-lg font-bold">{picked.locker_number}번 사물함</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {picked.student_name ? `지금 ${picked.student_name} 학생이 사용 중` : '비어 있습니다'}
              </p>
            </div>

            {picked.student_id ? (
              <div className="p-4 space-y-2">
                <button onClick={() => doRelease(picked)} disabled={busy}
                        className="w-full px-4 py-2.5 bg-red-600 text-white rounded-lg disabled:opacity-50">
                  이 사물함 비우기
                </button>
                <button onClick={() => setPicked(null)} className="w-full px-4 py-2.5 border rounded-lg">닫기</button>
              </div>
            ) : (
              <>
                <div className="p-4 border-b">
                  <input value={search} onChange={(e) => setSearch(e.target.value)}
                         placeholder="학생 이름으로 찾기"
                         className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredMembers.length === 0 ? (
                    <p className="p-6 text-center text-sm text-gray-500">학생이 없습니다.</p>
                  ) : filteredMembers.map((m) => {
                    const sid = m.user_id || m.id || '';
                    return (
                      <button key={sid} onClick={() => doAssign(sid)} disabled={busy}
                              className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b disabled:opacity-50">
                        <span className="font-medium">{m.name}</span>
                        {m.grade && <span className="ml-2 text-xs text-gray-500">{m.grade}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="p-4 border-t">
                  <button onClick={() => setPicked(null)} className="w-full px-4 py-2.5 border rounded-lg">닫기</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LockerManagement;
